import { action, makeObservable, observable, runInAction } from 'mobx';
import { setupMobX } from '../../../../../util/setupMobX';
import {
  CheckListId,
  ErrorCode,
  storeRepeatedChecklistResDto,
} from '@web/common';
import type {
  CannabisConsumptionQuestionnaireResult,
  RepeatedCheckListDataSchema,
  RepeatedQuestionnaireFilloutStatusResDto,
  StoreRepeatedChecklistReqDto,
  StoreRepeatedChecklistResDto,
} from '@web/common';

import {
  getMyRepeatedQuestionnaireChecklist,
  getRepeatedQuestionnaireFilloutStatus,
  storeMyRepeatedQuestionnaireChecklist,
} from '../../../../../request/authenticated-requests/repeated-checklist';
import {
  showErrorNotification,
  showSuccessNotification,
} from '../../../../../component/notification';
import { defaultRepeatedQuestionnaireChecklist } from './defaultRepeatedQuestionnaireChecklist';

export const allowedItems = [
  CheckListId.CannabisConsumptionQuestionnaire,
  CheckListId.SF12Questionnaire,
];

type ResultProps =
  | {
      id: CheckListId.CannabisConsumptionQuestionnaire;
      result: CannabisConsumptionQuestionnaireResult;
      userId?: string;
    }
  | {
      id: CheckListId.SF12Questionnaire;
      result: null;
      userId?: string;
    };

export class RepeatedQuestionnaireStore {
  @observable public showSimpleChecklist: boolean = false;
  @observable public loadingChecklist: boolean = true;
  @observable public allChecked: boolean = false;
  @observable public checklist: null | RepeatedCheckListDataSchema = null;
  @observable
  public repeatedQuestionnaireFilloutStatus: RepeatedQuestionnaireFilloutStatusResDto =
    {
      sf12: true, // true, so the dialog is not shown while loading
      cudit: true,
    };
  @observable private container: null | StoreRepeatedChecklistResDto = null;

  constructor() {
    makeObservable(this);
  }

  @action public getFilloutStatus() {
    getRepeatedQuestionnaireFilloutStatus()
      .then((res) =>
        runInAction(() => (this.repeatedQuestionnaireFilloutStatus = res)),
      )
      .catch(() => showErrorNotification());
  }

  @action public loadChecklist() {
    this.loadingChecklist = true;
    getMyRepeatedQuestionnaireChecklist()
      .then(async (res) => await this.parseChecklist(res))
      .catch(() => showErrorNotification())
      .finally(() =>
        runInAction(() => {
          this.loadingChecklist = false;
        }),
      );
  }

  @action public async checkItem(
    id:
      | CheckListId.SF12Questionnaire
      | CheckListId.CannabisConsumptionQuestionnaire,
    checkState: boolean,
  ) {
    await this.setChecklistItemState(id, checkState);
  }

  @action public async storeQuestionnaireResults({ id, result }: ResultProps) {
    if (!this.container) {
      showErrorNotification(ErrorCode.CannotSaveQuestionnaireResult);
      return;
    }
    const item = this.container.data?.[id];
    if (!item) {
      showErrorNotification(ErrorCode.CannotSaveQuestionnaireResult);
      return;
    }
    item.done = true;
    item.questionnaireResult = result;
    return this.storeOnboardingChecklist(this.container);
  }

  private updateData(res: StoreRepeatedChecklistResDto) {
    runInAction(() => {
      this.container = res;
      this.checklist = res.data ?? null;
      this.checkIfAllChecked(res);
    });
    this.getFilloutStatus();
  }

  private async storeOnboardingChecklist(
    checklist: StoreRepeatedChecklistReqDto,
  ) {
    return storeMyRepeatedQuestionnaireChecklist(checklist)
      .then((res) => {
        this.updateData(res);

        if (!res.data) return;
        const allChecked = Object.values(res.data).every(
          (item) => item.done === true,
        );
        if (allChecked) showSuccessNotification();
      })
      .catch(() => showErrorNotification());
  }

  private setChecklistItemState(
    id:
      | CheckListId.SF12Questionnaire
      | CheckListId.CannabisConsumptionQuestionnaire,
    checkState: boolean,
  ) {
    if (!this.container) {
      showErrorNotification(ErrorCode.CannotCheckItem);
      return;
    }

    const item = this.container.data?.[id];
    if (!item) {
      showErrorNotification(ErrorCode.CannotCheckItem);
      return;
    }
    item.done = checkState;
    return this.storeOnboardingChecklist(this.container);
  }

  private checkIfAllChecked(checkList: StoreRepeatedChecklistResDto) {
    if (!checkList.data) return;

    const checklistItems = Object.entries(checkList.data);
    const filteredList = checklistItems.filter(([id]) =>
      allowedItems.includes(id as CheckListId),
    );
    this.allChecked = filteredList.every(([, item]) => item.done);
  }

  private parseChecklist(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    checklistData: any,
  ) {
    const isEmpty = (obj: object) => Object.keys(obj).length === 0;

    if (isEmpty(checklistData)) {
      return this.storeOnboardingChecklist({
        data: defaultRepeatedQuestionnaireChecklist,
      });
    }

    const parsed = storeRepeatedChecklistResDto.parse(checklistData);
    this.updateData(parsed);
  }
}

const { provider, useStore } = setupMobX<RepeatedQuestionnaireStore>();
export const useRepeatedQuestionnaireStore = useStore;
export const RepeatedQuestionnaireStoreProvider = provider;
